import { Notification } from '@ecosystem/newron-design-system/build/exports/overlays';

export type Params = Record<string, string[]>;

export enum Periodicity {
  Monthly = 'Monthly',
  Weekly = 'Weekly',
}

export enum ChangeType {
  percent = 'percent',
  absolute = 'absolute',
}

export enum CurrencyOption {
  eur = 'eur',
  local = 'local',
  usd = 'usd',
}

export enum DataType {
  String = 'String',
  Integer = 'Integer',
  Date = 'Date',
  Decimal = 'Decimal',
}

export enum FormatHint {
  Percentage = 'Percentage',
  PercentagePoints = 'PercentagePoints',
  Money = 'Money',
  Index = 'Index',
  MonthYear = 'MonthYear',
  Month = 'Month',
  Year = 'Year',
  WholeMoney = 'WholeMoney',
}

export enum SortType {
  Absolute = 'Absolute',
  Change = 'Change',
  Alpha = 'Alpha',
  Date = 'Date',
}

export enum SortDirection {
  ascending = 'ascending',
  descending = 'descending',
}

export type SortOption = {
  sortType: SortType;
  sortKey: string;
  label: string;
  isDefault?: boolean;
  defaultDirection?: SortDirection;
};

export type SortingInfo = {
  columnName: string;
  sortKey: string;
  sortType: SortType;
  direction: SortDirection;
  label: string;
  sortIndex: number;
};

export type DataWarningKpi = { name: string; label: string };

export type DataWarningDetails = { level: string; kpis?: DataWarningKpi[] };

export type DataWarning = [string, DataWarningDetails];

export enum GenericWarningScope {
  Global = 'GLOBAL',
  Local = 'LOCAL',
}

export type FieldWarning = GenericWarning & {
  relatedFields?: string[];
};

export type GenericWarning = {
  level: Notification;
  message: string;
  scope?: GenericWarningScope;
};

export enum ColumnType {
  Identifier = 'Identifier',
  Kpi = 'Kpi',
  KpiWithChange = 'KpiWithChange',
  Text = 'Text',
  Date = 'Date',
  Number = 'Number',
  UpsellKpiAvailability = 'UpsellKpiAvailability',
}

export enum ColumnRelationship {
  Change = 'Change',
  Share = 'Share',
  Description = 'Description',
  QueryArgs = 'QueryArgs',
  Detail = 'Detail',
}

interface Column {
  type: ColumnType;
  name: string;
  label: string;
  dataType: DataType;
  arrayLength: number | null;
  belongsTo: string;
  canAggregate: boolean;
  formatHint?: FormatHint;
  sortOptions?: SortOption[];
  customType?: string;
  relatedTo?: string;
  relationship?: ColumnRelationship;
  enrichment?: string;
  isFixed?: boolean;
  isChange?: boolean;
}

export interface IdentifierColumn extends Column {
  idField: string;
  identifier: string[];
  subIdentifier?: string[];
}

export type KpiColumn = Column;

export type TextColumn = Column;

export type DateColumn = Column;

export type NumberColumn = Column;

export interface KpiWithChangeColumn extends Column {
  changeName: string;
  changeLabel: string;
  changeFormatHint?: FormatHint;
  changeDataType: DataType;
}

export type DataColumn = IdentifierColumn | KpiColumn | KpiWithChangeColumn | TextColumn | DateColumn | NumberColumn;

export type AvailableDataColumn = DataColumn & {
  enrichment: string;
};

export type AvailableColumns = {
  enrichment: string;
  columns: DataColumn[];
};

export type DataCellValue = string | number | (number | null)[] | null;

export type DataRow = {
  values: Record<string, DataCellValue>;
  hasData?: boolean;
};

export type DataRows = DataRow[];

export type Period = {
  periodSeq: number;
  year: number;
  positionInYear: number;
  name: string;
  periodicity: Periodicity;
};

export type TimeFrame = {
  periodicity: Periodicity;
  periods: Period[];
};

export type GroupByResultType = {
  type: 'group_by';
  groupBy: string[];
  currency: string;
  timeFrame: TimeFrame;
};

export type MarketInfoResultType = {
  type: 'market_info';
  timeFrame: TimeFrame;
};

export type ResultType = GroupByResultType | MarketInfoResultType;

export type ResultSet = {
  warnings?: FieldWarning[];
  type: ResultType;
  columns: DataColumn[];
  data: DataRows;
  availableColumns: AvailableColumns[];
  pipeline?: string;
  upsellKpisIndicator?: boolean;
};

export type DataResults = {
  requestId: string;
  results: ResultSet[];
  warnings: GenericWarning[];
};

export type DataFetchLoading = {
  isDataLoading?: boolean;
  isEnrichDataLoading?: boolean;
};

export enum PeriodAggregation {
  Week = 'Week',
  Month = 'Month',
  Quarter = 'Quarter',
  RollingQuarter = 'RollingQuarter',
  Year = 'Year',
  MAT = 'MAT',
  YTD = 'YTD',
  Custom = 'Custom',
}
